import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { scoringConfigApi, leadScoreApi } from '../../api/endpoints'
import { SegmentBadge } from '../ui/Badge'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CategoryScore {
  earned: number
  budget: number
  label?: string
}

interface FactorDetail {
  label: string
  points: number
  source?: string | null
  call_id?: number | null
}

interface CategoryBreakdown {
  label: string
  earned: number
  budget: number
  factors: FactorDetail[]
}

interface ScoreHistoryEntry {
  id: number
  old_score: number | null
  new_score: number | null
  old_segment: string | null
  new_segment: string | null
  trigger: string
  created_at: string
}

interface ScoringConfig {
  categories: Record<string, { label: string; max: number }>
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CATEGORY_COLORS: Record<string, { bar: string; text: string }> = {
  business_fit: { bar: 'bg-blue-500', text: 'text-blue-600' },
  engagement: { bar: 'bg-green-500', text: 'text-green-600' },
  urgency: { bar: 'bg-amber-500', text: 'text-amber-600' },
  interaction: { bar: 'bg-purple-500', text: 'text-purple-600' },
}

const DEFAULT_COLORS = { bar: 'bg-indigo-500', text: 'text-indigo-600' }

function parseSafe<T>(value: unknown): T | null {
  if (!value) return null
  if (typeof value === 'object') return value as T
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T
    } catch {
      return null
    }
  }
  return null
}

function pct(earned: number, budget: number): number {
  if (budget <= 0) return 0
  return Math.min(100, Math.round((earned / budget) * 100))
}

function fmtShortDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ProgressBar({ value, max, colorClass }: { value: number; max: number; colorClass: string }) {
  const percentage = pct(value, max)
  return (
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${colorClass}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  )
}

function CategorySection({
  categoryKey,
  breakdown,
}: {
  categoryKey: string
  breakdown: CategoryBreakdown
}) {
  const colors = CATEGORY_COLORS[categoryKey] || DEFAULT_COLORS
  const percentage = pct(breakdown.earned, breakdown.budget)
  const nearMax = percentage >= 90

  return (
    <div className="space-y-1.5">
      {/* Category header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{breakdown.label}</span>
        <span className={`text-xs font-medium ${colors.text}`}>
          {breakdown.earned}/{breakdown.budget} ({percentage}%)
        </span>
      </div>

      {/* Progress bar */}
      <ProgressBar value={breakdown.earned} max={breakdown.budget} colorClass={colors.bar} />

      {/* Factors */}
      {breakdown.factors.length > 0 && (
        <div className="pl-3 border-l-2 border-gray-100 space-y-0.5 mt-1">
          {breakdown.factors.map((factor, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-gray-600 flex items-center gap-1.5">
                <span className="text-gray-300">{i < breakdown.factors.length - 1 ? '\u251C' : '\u2514'}</span>
                {factor.label}
                {factor.call_id && (
                  <a href={`/voip?call=${factor.call_id}`} className="text-indigo-500 hover:text-indigo-700 underline">
                    call #{factor.call_id}
                  </a>
                )}
              </span>
              <span className={`font-medium ${factor.points > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                {factor.points > 0 ? `+${factor.points}` : factor.points}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Near-max warning */}
      {nearMax && (
        <div className="flex items-center gap-1 text-xs text-amber-600 mt-0.5">
          <AlertTriangle size={12} />
          <span>Near maximum ({percentage}%)</span>
        </div>
      )}
    </div>
  )
}

function ScoreHistoryTimeline({ entries }: { entries: ScoreHistoryEntry[] }) {
  if (entries.length === 0) return null

  return (
    <div className="mt-4 pt-3 border-t border-gray-100">
      <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Score History</h4>
      <div className="space-y-1.5">
        {entries.slice(0, 10).map((entry, i) => {
          const score = entry.new_score ?? 0
          const delta = entry.old_score != null && entry.new_score != null
            ? Math.round(entry.new_score - entry.old_score)
            : 0
          return (
            <div key={entry.id ?? i} className="flex items-center gap-2 text-xs">
              <span className="text-gray-400 w-14 flex-shrink-0">{fmtShortDate(entry.created_at)}</span>
              <span className="font-semibold text-gray-700 w-8 text-right">{Math.round(score)}</span>
              <SegmentBadge segment={entry.new_segment} />
              {delta !== 0 && (
                <span className={`flex items-center gap-0.5 ${delta > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {delta > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {delta > 0 ? `+${delta}` : delta}
                </span>
              )}
              {delta === 0 && i > 0 && (
                <span className="flex items-center gap-0.5 text-gray-400">
                  <Minus size={12} />
                </span>
              )}
              {entry.trigger && (
                <span className="text-gray-400 truncate">{entry.trigger}</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Gaps extraction — find factors worth points that this lead has NOT earned
// ---------------------------------------------------------------------------

function extractGaps(
  config: ScoringConfig | null,
  categoryScores: Record<string, CategoryScore> | null,
  breakdown: Record<string, CategoryBreakdown> | null,
): string[] {
  if (!categoryScores) return []
  const gaps: string[] = []

  // Look for categories with room to grow
  for (const [key, raw] of Object.entries(categoryScores)) {
    const isPlainNumber = typeof raw === 'number'
    const earned = isPlainNumber ? (raw as unknown as number) : (raw as CategoryScore).earned ?? 0
    const configCat = config?.categories?.[key]
    const budget = isPlainNumber
      ? ((configCat?.max) || 0)
      : ((raw as CategoryScore).budget ?? 0)
    const remaining = Math.round(budget - earned)
    if (remaining > 5) {
      const label = configCat?.label || (!isPlainNumber ? (raw as CategoryScore).label : null) || key
      gaps.push(`${label} has ${remaining} pts available`)
    }
  }

  // Also check breakdown for specific un-earned factors if available
  if (breakdown) {
    for (const cat of Object.values(breakdown)) {
      for (const factor of cat.factors) {
        if (factor.points === 0) {
          gaps.push(factor.label)
        }
      }
    }
  }

  return gaps.slice(0, 3)
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

interface ScoreCardProps {
  lead: Record<string, unknown>
  collapsed?: boolean
}

export default function ScoreCard({ lead, collapsed = true }: ScoreCardProps) {
  const [expanded, setExpanded] = useState(!collapsed)

  const categoryScores = parseSafe<Record<string, CategoryScore>>(lead.category_scores)
  const breakdown = parseSafe<Record<string, CategoryBreakdown>>(lead.score_breakdown)

  const leadId = (lead.lead_id as string) || (lead.id as number)

  // Load scoring config for labels and budgets
  const { data: configData } = useQuery({
    queryKey: ['scoring-config'],
    queryFn: () => scoringConfigApi.getActive(),
    staleTime: 5 * 60 * 1000,
  })
  const config = parseSafe<ScoringConfig>(configData?.data?.config) || null

  // Load score history (only when expanded)
  const { data: historyData } = useQuery({
    queryKey: ['lead-score-history', leadId],
    queryFn: () => leadScoreApi.history(leadId),
    enabled: expanded && !!leadId,
    staleTime: 60 * 1000,
  })

  const score = lead.score as number | null
  const segment = lead.quality_segment as string | null
  const leadConfigVersion = lead.score_config_version as number | null | undefined
  const activeConfigVersion = configData?.data?.version as number | null | undefined
  const scoreOutdated = activeConfigVersion != null && activeConfigVersion > 0
    && (leadConfigVersion == null || leadConfigVersion < activeConfigVersion)

  // Nothing to show if no score data at all
  if (score == null && !categoryScores) return null

  const gaps = extractGaps(config, categoryScores, breakdown)
  const history = (historyData?.data || []) as ScoreHistoryEntry[]

  // Build merged category breakdowns from categoryScores + breakdown + config
  // category_scores can be either {key: number} or {key: {earned, budget}}
  const mergedCategories: Record<string, CategoryBreakdown> = {}
  if (categoryScores) {
    for (const [key, raw] of Object.entries(categoryScores)) {
      const configCat = config?.categories?.[key]
      const breakdownCat = breakdown?.[key]
      // Handle both formats: plain number or {earned, budget} object
      const isPlainNumber = typeof raw === 'number'
      const earned = isPlainNumber ? (raw as unknown as number) : (raw as CategoryScore).earned ?? 0
      const budget = isPlainNumber
        ? (configCat?.max ?? 0)
        : ((raw as CategoryScore).budget ?? 0)
      mergedCategories[key] = {
        label: breakdownCat?.label || configCat?.label || (!isPlainNumber ? (raw as CategoryScore).label : null) || key.replace(/_/g, ' '),
        earned,
        budget,
        factors: breakdownCat?.factors || [],
      }
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      {/* Collapsed header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between group"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-xs font-medium text-gray-500 uppercase">Score Breakdown</h3>
          {score != null && (
            <span className="text-lg font-bold text-gray-900">{Math.round(score)}</span>
          )}
          <SegmentBadge segment={segment} />
          {scoreOutdated && (
            <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded animate-pulse">Score outdated</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!expanded && gaps.length > 0 && (
            <span className="text-xs text-gray-400 hidden sm:block">
              {gaps.join(' \u2022 ')}
            </span>
          )}
          {expanded ? (
            <ChevronUp size={16} className="text-gray-400 group-hover:text-gray-600" />
          ) : (
            <ChevronDown size={16} className="text-gray-400 group-hover:text-gray-600" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Summary row — same as old score breakdown but compact */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{score != null ? Math.round(score) : '\u2014'}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            {Object.entries(mergedCategories).slice(0, 3).map(([key, cat]) => {
              const colors = CATEGORY_COLORS[key] || DEFAULT_COLORS
              return (
                <div key={key} className="text-center">
                  <p className={`text-xl font-semibold ${colors.text}`}>{cat.earned}</p>
                  <p className="text-xs text-gray-500">{cat.label} (0-{cat.budget})</p>
                </div>
              )
            })}
          </div>

          {/* Category breakdowns with progress bars */}
          {Object.keys(mergedCategories).length > 0 && (
            <div className="space-y-4 pt-2 border-t border-gray-100">
              {Object.entries(mergedCategories).map(([key, cat]) => (
                <CategorySection key={key} categoryKey={key} breakdown={cat} />
              ))}
            </div>
          )}

          {/* Gaps */}
          {gaps.length > 0 && (
            <div className="pt-2 border-t border-gray-100">
              <h4 className="text-xs font-medium text-gray-500 uppercase mb-1.5">Opportunities to improve</h4>
              <div className="flex flex-wrap gap-1.5">
                {gaps.map((gap, i) => (
                  <span
                    key={i}
                    className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full"
                  >
                    {gap}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Score history timeline */}
          <ScoreHistoryTimeline entries={history} />
        </div>
      )}
    </div>
  )
}
